/**
 * @file billWindowActions.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-16
 * @description Every action the bill window (BillModal) offers, worked out in
 *              one place: what it is called, its icon, which group of the
 *              action bar it sits in, and — when it does not apply — that it is
 *              greyed and why. Every action is always in the list, on every kind
 *              of bill (disable, never hide): a ragged set of buttons reads as a
 *              different kind of document rather than one where an action does
 *              not apply.
 *
 *              Cam, 15 Oct 2026: the window was two panels and its buttons no
 *              longer fitted the left one; it is one panel now, with an action
 *              bar drawn from this list. The rules are the window's own, moved
 *              here unchanged — Edit follows the server's edit_context, Status
 *              statusChangeAllowed, the credit actions utils/supplierCredits.
 *              The four that used to be hidden rather than greyed (Edit with no
 *              editor to open, Status on a bill keyed in Xero, Find in Search
 *              Bills where it is behind the window, Download with no PDF) are
 *              now greyed with their reason.
 */

import { statusChangeAllowed } from './billStatus';
import { isSupplierCredit, allocateRowBlock, allocateLabel, addCreditRowBlock } from './supplierCredits';
import { isVoided } from './voidedDocument';

/** The action bar's groups, in the order they are drawn. 'pdf' sits with the attachment. */
export const BILL_ACTION_GROUPS = Object.freeze([
  Object.freeze({ key: 'document', label: 'Bill' }),
  Object.freeze({ key: 'pos', label: 'POs and invoices' }),
  Object.freeze({ key: 'credits', label: 'Credits' }),
  Object.freeze({ key: 'find', label: 'Find' }),
  Object.freeze({ key: 'pdf', label: 'Supplier\'s PDF' }),
]);

const up = (v) => String(v || '').toUpperCase();

/**
 * @param {object} bill — GET /api/bills/:id's bill
 * @param {object} ctx
 * @param {boolean} ctx.hasEditor — the host gave the window an onEdit (Search Bills)
 * @param {boolean} ctx.canFind — "Find in Search Bills" has somewhere to go
 * @param {boolean} ctx.canNavigate — the host gave the window onNavigate
 * @param {{count:number, block?:string}|null} ctx.poCostInfo — /po-cost-differences, null unread
 * @param {{count:number}|null} ctx.invoiceInfo — /invoice-proposals, null unread
 * @param {boolean} ctx.uploadingPdf — a PDF is being saved now
 * @returns {Array<{ key, group, icon, label, title, disabled, badge, writes?, opensVoided?, spinning? }>}
 *   `writes` — it changes something (data-write-action: greyed for view-only users).
 */
export function billWindowActions(bill, ctx = {}) {
  if (!bill) return [];
  const {
    hasEditor = false, canFind = false, canNavigate = false,
    poCostInfo = null, invoiceInfo = null, uploadingPdf = false,
  } = ctx;
  const voided = isVoided(bill.status);
  const credit = isSupplierCredit(bill);
  const isCreditNote = up(bill.transaction_type) === 'CREDIT';
  const actions = [];

  // ── Bill ──────────────────────────────────────────────────────────────────
  const editMode = bill.edit_context?.mode || null;
  const editable = !voided && ['local', 'xero_draft', 'patch'].includes(editMode);
  const editBlock = bill.edit_context?.block || 'Cannot edit this bill here';
  let editTitle;
  let editDisabled = false;
  if (voided) {
    editTitle = 'Voided — it cannot be edited or restored. Raise a copy, or email accounts';
  } else if (!editable) {
    editDisabled = true;
    editTitle = `Cannot edit — ${editBlock}`;
  } else if (!hasEditor) {
    editDisabled = true;
    editTitle = 'Open it from Search Bills to edit it';
  } else {
    editTitle = editMode === 'patch' ? 'Edit — only what you change is sent to Xero' : 'Edit';
  }
  actions.push({ key: 'edit', group: 'document', icon: 'edit', label: 'Edit',
    title: editTitle, disabled: editDisabled, badge: null, opensVoided: voided });

  const statusRule = statusChangeAllowed(bill);
  actions.push({ key: 'status', group: 'document', icon: 'rule', label: 'Status',
    title: voided
      ? 'Voided — its status cannot change. Raise a copy, or email accounts'
      : statusRule.ok ? 'Change status / Queried' : `Cannot change the status — ${statusRule.reason}`,
    disabled: !voided && !statusRule.ok, badge: null, opensVoided: voided });

  // ── POs and invoices ──────────────────────────────────────────────────────
  const linkCount = isCreditNote
    ? (bill.credit_links?.pos?.length || 0) + (bill.credit_links?.bills?.length || 0)
    : (bill.linked_pos?.length || 0) + (bill.haulage_pos?.length || 0);
  actions.push({ key: 'pos', group: 'pos', icon: 'link',
    label: isCreditNote ? 'Relates to' : 'POs',
    title: isCreditNote
      ? 'The POs and bills this credit relates to (a reference only, kept here)'
      : 'Link POs to this bill, or take them off — goods or haulage for a PO (kept here only)',
    disabled: false, badge: linkCount > 0 ? linkCount : null, writes: true });

  let poTitle;
  let poDisabled = false;
  if (credit) {
    poDisabled = true;
    poTitle = isCreditNote
      ? 'A supplier credit never changes a PO\'s cost — the PO keeps the bill\'s cost; allocate the credit to the bill instead'
      : 'An overpayment or prepayment sets no PO\'s cost — allocate it to the supplier\'s bills instead';
  } else if (poCostInfo && poCostInfo.count === 0) {
    poDisabled = true;
    poTitle = poCostInfo.block || 'This bill and its POs already agree on cost';
  } else {
    poTitle = bill.xero_push_info?.xero_edit?.po_costs_differ
      ? 'Changed in Xero since it was sent — its POs were not changed. Choose which PO lines take the bill\'s cost'
      : 'Choose which PO lines take this bill\'s cost';
  }
  actions.push({ key: 'updatePo', group: 'pos', icon: 'price_check', label: 'Update PO from this bill…',
    title: poTitle, disabled: poDisabled, writes: true,
    badge: !credit && poCostInfo?.count > 0 ? poCostInfo.count : null });

  const noInvoice = !!invoiceInfo && invoiceInfo.count === 0;
  actions.push({ key: 'updateInvoice', group: 'pos', icon: 'receipt_long', label: 'Update invoice from supplied qty…',
    title: noInvoice
      ? 'No invoice of this bill\'s orders needs changing for its supplied quantities'
      : 'An invoice was raised on the old supplied quantity — see the changes it needs',
    disabled: noInvoice, badge: invoiceInfo?.count > 0 ? invoiceInfo.count : null, writes: true });

  // ── Credits ───────────────────────────────────────────────────────────────
  const allocBlock = allocateRowBlock(bill);
  actions.push({ key: 'allocate', group: 'credits', icon: 'credit_score', label: allocateLabel(bill),
    title: allocBlock || (credit ? 'Allocate this credit to the supplier\'s bills' : 'Apply one of the supplier\'s credits to this bill'),
    disabled: !!allocBlock, badge: null, writes: true });

  const creditBlock = addCreditRowBlock(bill);
  actions.push({ key: 'addCredit', group: 'credits', icon: 'note_add', label: 'Add credit against this bill',
    title: creditBlock || (!canNavigate ? 'Open it from Search Bills to add a credit' : 'Add a supplier credit against this bill (the supplier and this bill filled in)'),
    disabled: !!creditBlock || !canNavigate, badge: null, writes: true });

  // ── Find ──────────────────────────────────────────────────────────────────
  // Found by the supplier's invoice number (supplier_reference) — bill_number is never filled.
  const findRef = bill.supplier_reference || bill.bill_number || null;
  const findBlock = !findRef
    ? 'This bill has no invoice number to find it by'
    : !canFind ? 'You are on Search Bills — its row is behind this window' : null;
  actions.push({ key: 'find', group: 'find', icon: 'manage_search', label: 'Find in Search Bills',
    title: findBlock || `Show ${findRef} in Search Bills`, disabled: !!findBlock, badge: null });

  // ── The supplier's PDF ───────────────────────────────────────────────────
  const hasPdf = !!bill.attachment_path;
  actions.push({ key: 'attachPdf', group: 'pdf', icon: uploadingPdf ? 'progress_activity' : 'upload_file',
    spinning: uploadingPdf,
    label: uploadingPdf ? 'Saving PDF…' : (hasPdf ? 'Replace PDF' : 'Attach PDF'),
    title: uploadingPdf ? 'Saving the PDF…' : (hasPdf ? 'Replace the supplier\'s PDF' : 'Attach the supplier\'s PDF'),
    disabled: uploadingPdf, badge: null, writes: true });
  actions.push({ key: 'downloadPdf', group: 'pdf', icon: 'download', label: 'Download',
    title: hasPdf ? 'Download the supplier\'s PDF' : 'No PDF attached yet — attach one first',
    disabled: !hasPdf, badge: null });

  return actions;
}

/** The actions in one group, in order. */
export function actionsInGroup(actions, group) {
  return actions.filter((a) => a.group === group);
}
